import React from 'react'
import { MenuItem, TextField } from '@mui/material'

export type CurrencyValue = '' | 'USD' | 'CAD' | 'EUR'
export type FbTokenValue = '' | 'LP'
export type GranularityValue = '' | 'WEEKLY' | 'MONTHLY'

export interface BrandAdvancedValues {
  currencyType: CurrencyValue
  fbAccessTokenType: FbTokenValue
  dataGranularity: GranularityValue
  minAdSpend: string
  insightsThreshold: string
}

export const emptyBrandAdvanced = (): BrandAdvancedValues => ({
  currencyType: '',
  fbAccessTokenType: '',
  dataGranularity: '',
  minAdSpend: '',
  insightsThreshold: '',
})

/** The shape passed to the create/update/setup brand mutations. */
export interface BrandConfigInput {
  currencyType: 'USD' | 'CAD' | 'EUR' | null
  fbAccessTokenType: 'LP' | null
  dataGranularity: 'WEEKLY' | 'MONTHLY' | null
  minAdSpend: number | null
  insightsThreshold: number | null
}

const toNumberOrNull = (value: string): number | null => {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

export const toBrandConfigInput = (
  values: BrandAdvancedValues
): BrandConfigInput => ({
  currencyType: values.currencyType === '' ? null : values.currencyType,
  fbAccessTokenType:
    values.fbAccessTokenType === '' ? null : values.fbAccessTokenType,
  dataGranularity:
    values.dataGranularity === '' ? null : values.dataGranularity,
  minAdSpend: toNumberOrNull(values.minAdSpend),
  insightsThreshold: toNumberOrNull(values.insightsThreshold),
})

interface BrandAdvancedFieldsProps {
  values: BrandAdvancedValues
  onChange: (patch: Partial<BrandAdvancedValues>) => void
}

/**
 * The brand "advanced" settings — currency, FB access-token type, and the
 * structured config keys (data granularity, min ad spend, insights threshold).
 * All optional. Used by both the Edit Brand dialog and the setup wizard.
 */
export const BrandAdvancedFields = ({
  values,
  onChange,
}: BrandAdvancedFieldsProps) => (
  <div>
    <div className="flex gap-2">
      <TextField
        select
        fullWidth
        margin="normal"
        label="Currency"
        value={values.currencyType}
        onChange={(event) =>
          onChange({ currencyType: event.target.value as CurrencyValue })
        }
      >
        <MenuItem value="">
          <em>Default (USD)</em>
        </MenuItem>
        <MenuItem value="USD">USD</MenuItem>
        <MenuItem value="CAD">CAD</MenuItem>
        <MenuItem value="EUR">EUR</MenuItem>
      </TextField>
      <TextField
        select
        fullWidth
        margin="normal"
        label="FB Access Token Type"
        value={values.fbAccessTokenType}
        onChange={(event) =>
          onChange({ fbAccessTokenType: event.target.value as FbTokenValue })
        }
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        <MenuItem value="LP">LP</MenuItem>
      </TextField>
    </div>
    <TextField
      select
      fullWidth
      margin="normal"
      label="Data Granularity"
      value={values.dataGranularity}
      onChange={(event) =>
        onChange({ dataGranularity: event.target.value as GranularityValue })
      }
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      <MenuItem value="WEEKLY">Weekly</MenuItem>
      <MenuItem value="MONTHLY">Monthly</MenuItem>
    </TextField>
    <div className="flex gap-2">
      <TextField
        fullWidth
        margin="normal"
        type="number"
        label="Min Ad Spend"
        value={values.minAdSpend}
        onChange={(event) => onChange({ minAdSpend: event.target.value })}
        inputProps={{ min: 0 }}
      />
      <TextField
        fullWidth
        margin="normal"
        type="number"
        label="Insights Threshold"
        value={values.insightsThreshold}
        onChange={(event) =>
          onChange({ insightsThreshold: event.target.value })
        }
        inputProps={{ min: 0 }}
      />
    </div>
  </div>
)

export default BrandAdvancedFields
